import type {JSX, ReactNode} from 'react'
import {useEffect} from 'react'
import {Controller, useFormContext} from 'react-hook-form'
import type {OptionProps, SingleValue} from 'react-select'
import ReactSelect from 'react-select'
import Image from 'next/image'
import {clsx} from 'clsx'
import {loadImageFromKontentAI} from '@/image-loader'
import {logError} from '@/services/logger/client'
import {ErrorMessage} from '../form'

interface OptionsType {
	value: string
	label: string
	icon?: string
	additionalText?: string
}

interface Option {
	value: string
	label?: string
}

interface SearchableSelectFieldProps {
	label: ReactNode
	name: string
	className?: string
	isOptional?: boolean
	requiredErrorMsg?: string
	defaultValue?: string
	onChange?: (value: string) => void
	disabled?: boolean
	options: OptionsType[]
}

export const SearchableSelectField = ({
	defaultValue = '',
	className = '',
	options,
	label,
	name,
	isOptional = false,
	requiredErrorMsg = '',
	onChange,
	disabled = false,
}: SearchableSelectFieldProps): JSX.Element => {
	const requiredMsg = requiredErrorMsg || `${String(label)} is required`
	const {control, trigger, formState} = useFormContext()

	useEffect(() => {
		let ignore = false

		async function validation(): Promise<void> {
			if (defaultValue && !ignore) {
				await trigger(name)
			}
		}

		validation().catch((error) => {
			logError(
				new Error(`SearchableSelectField Validation failed: ${error}`)
			)
		})

		return () => {
			ignore = true
		}
	}, [defaultValue, name, trigger])

	// eslint-disable-next-line react/no-unstable-nested-components -- nested component
	const IconOption = ({
		data,
		innerProps,
	}: OptionProps<OptionsType>): JSX.Element => {
		return (
			<div {...innerProps} className="flex items-center py-2">
				{data.icon ? (
					<div className="mr-4 h-10 w-10 flex-shrink-0">
						<Image
							alt={`${data.label} icon`}
							height={40}
							loader={loadImageFromKontentAI}
							src={data.icon}
							width={40}
						/>
					</div>
				) : null}
				<div className="flex-col">
					<p>{data.label} </p>
					{data.additionalText ? (
						<p className="pt-1 text-xs">{data.additionalText}</p>
					) : null}
				</div>
			</div>
		)
	}

	const customFilter = (
		option: {data: OptionsType},
		searchText: string
	): boolean => {
		if (
			option.data.label
				.toLowerCase()
				.includes(searchText.toLowerCase()) ||
			option.data.additionalText
				?.toLowerCase()
				.includes(searchText.toLowerCase())
		) {
			return true
		}
		return false
	}

	return (
		<div className={clsx('relative mb-4', className)}>
			<label className="w-full text-xs text-pet-primary" htmlFor={name}>
				<span>
					{label}
					{!isOptional && <span className="text-red-600">*</span>}
				</span>
				<div className="relative mt-2">
					<Controller
						control={control}
						defaultValue={defaultValue}
						name={name}
						render={({field}) => (
							<ReactSelect
								{...field}
								classNames={{
									control: () =>
										clsx(
											'!rounded-md !bg-white !opacity-1 !text-sm !font-normal !text-pet-primary !border !py-[4px] !mb-1',
											formState.errors[name]
												? '!border-[#ea0101]'
												: '!border-[#a0aec0]'
										),
									singleValue: () =>
										'!text-sm !font-normal !text-pet-primary',
									placeholder: () => '!text-sm !font-normal',
									input: () => '!text-sm !p-0 !m-0',
									indicatorsContainer: () => '!p-0',
									indicatorSeparator: () => '!w-0',
									dropdownIndicator: () =>
										'!p-2 !text-mid-grey',
								}}
								components={{Option: IconOption}} // Use the custom Option component here
								filterOption={customFilter}
								id={name}
								isDisabled={disabled}
								isMulti={false}
								name={name}
								onChange={(e: SingleValue<Option>) => {
									if (!e) return
									field.onChange(e.value)
									onChange?.(e.value)
								}}
								options={options}
								placeholder="Start typing to filter"
								value={options.find(
									(i) => i.value === field.value
								)}
							/>
						)}
						rules={{required: !isOptional}}
					/>
					{formState.errors[name] ? (
						<ErrorMessage message={requiredMsg} />
					) : null}
				</div>
			</label>
		</div>
	)
}
